import React, { useEffect, useRef, useState } from "react";
import { setJourneyToken } from "@/lib/journeyToken";
import Div100vh from "react-div-100vh";
// Was mistakenly `import { config } from "dotenv"` — dotenv's `config` is a FUNCTION, so
// `config.OTP_LENGTH` was silently undefined. This is the real app config module (src/config.js).
import config from "@/config";
import { useTheme } from "@/b2b/context/ThemeContext";
import DynamicInputForm from "@/b2b/components/DynamicInputForm.jsx";
import PrimaryButton from "@/b2b/components/PrimaryButton";
import Header from "@/b2b/components/Header";
import CustomDrawer from "@/components/common/drawer";
import GoBackButton from "@/components/common/buttons/GoBackButton";
import { useJourneyLender } from "@/lib/journeyLender";
import { useRecoilValue } from "recoil";
import { lenderConfigAtom } from "@/recoil/atom/lenderConfigAtom";
import { lenderFromJourneyResponse } from "@/lib/journeyLenderCore";
import { pickWorkDetailsLenderKey, workDetailsRules } from "@/b2b/lib/workDetailsLender";
import OTPInput from "react-otp-input";
import { otpSendFailureMessage } from "../lib/otpSend";
import { navigationRules } from "@/b2b/config/partnerJourney";
import {
  isValidEmail,
  updateFunction,
  getDataB2B,
  fetchData,
  isWorkEmail,
  updateFunctionB2B,
} from "@/lib/helper";
import Loader from "@/components/common/Loader";
import { useNavigate } from "react-router-dom";
import { trackConsent } from "@/lib/apis";
import { Button } from "@/components/ui/button";
import moment from "moment";
import { applyWorkDetailsPrefill } from "@/b2b/lib/workDetailsPrefill";
import { workDetailsNextStep } from "@/b2b/lib/workDetailsNextStep";
import {
  activeEmploymentType,
  needsWorkEmailOtp,
  normaliseSalary,
  offeredEmploymentTypes,
  requiredWorkFields,
  salariedFields,
  workDetailsPayload,
} from "@/b2b/lib/employmentRules";

const salariedConfig = [
  {
    type: "text",
    label: "Company Name",
    name: "companyName",
    required: true,
    pattern: "^[a-zA-Z]+(?:s+[a-zA-Z]+){1,2}$",
  },
  {
    type: "text",
    label: "Job Role/Designation",
    name: "designation",
    required: true,
    pattern: "^[a-zA-Z]+(?:s+[a-zA-Z]+){1,2}$",
  },
  {
    type: "address",
    label: "Office Address",
    name: "officeAddress",
    required: true,
    pattern: "^[a-zA-Z]+(?:s+[a-zA-Z]+){1,2}$",
  },
  {
    type: "email",
    label: "Work Email",
    name: "workEmail",
    required: true,
    pattern: "[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+.[A-Za-z]{2,}",
  },
  {
    type: "text",
    label: "Monthly Salary (after deductions)",
    name: "income",
    required: true,
    pattern: "^[0-9]+$",
  },
];

const selfEmployedConfig = [
  {
    type: "text",
    label: "Business Name",
    name: "businessName",
    required: true,
    pattern: "^[a-zA-Z]+(?:s+[a-zA-Z]+){1,2}$",
  },
  {
    type: "text",
    label: "Annual Turnover (INR)",
    name: "turnover",
    required: true,
    pattern: "^[0-9]+$",
  },
  {
    type: "text",
    label: "Business Registration Year",
    name: "registrationYear",
    required: true,
    pattern: "^[0-9]{4}$",
  },
];

const internConfig = [
  {
    type: "text",
    label: "Internship Duration (in months)",
    name: "internshipDuration",
    required: true,
    pattern: "^[0-9]+$",
  },
];

const employmentTypes = [
  // { id: "INTERN", label: "Intern" },
  { id: "SALARIED", label: "Salaried" },
  { id: "SELF_EMPLOYED", label: "Self Employed" },
];

export default function WorkDetails({ leadData = {}, journeyLenderKey = null }) {
  // `leadData` is the pre-flight Lead/session — it carries no `lender`, so the journey's
  // lender comes from the journey itself; null (no "powered by" logo) until it has answered.
  // `rules`: what the CURRENT journey's lender asks of this screen, from its profile fields (never
  // its key). Review I1: never from the session cache — cpcore can move the journey to its fallback
  // lender during the work-details save, so the cache may name the lender it just left. The key is
  // this screen's own fresh `journey/full` read, else the one HomeScreen read just before routing
  // here (`journeyLenderKey`), else none → today's screen (work email asked and OTP-verified).
  const lenderConfig = useRecoilValue(lenderConfigAtom);
  const [fetchedLenderKey, setFetchedLenderKey] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchData("journey/full")
      .then((response) => {
        if (!cancelled) setFetchedLenderKey(lenderFromJourneyResponse(response));
      })
      .catch(() => {}); // stays unknown → today's screen
    return () => {
      cancelled = true;
    };
  }, []);
  const currentLenderKey = pickWorkDetailsLenderKey({
    fetched: fetchedLenderKey,
    fromHome: journeyLenderKey,
  });
  const rules = workDetailsRules(lenderConfig?.lenders, currentLenderKey);
  // Passing the current key also overwrites the session cache, so later screens stop reading the
  // lender the journey left.
  const lender = useJourneyLender(currentLenderKey);
  const navigate = useNavigate();
  const theme = useTheme();

  // The lender may not offer every type (`hiddenEmploymentTypes`). The tab the form uses is derived,
  // never stored, so a pre-filled or earlier choice of a hidden type falls back to an offered one.
  const [chosenEmploymentType, setEmploymentType] = useState("SALARIED");
  const offeredTypes = offeredEmploymentTypes(employmentTypes, rules);
  const employmentType = activeEmploymentType(chosenEmploymentType, offeredTypes);
  const [salaryReceivedCheck, setSalaryReceivedCheck] = useState(false);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const [verifying, setVerifying] = useState(false);
  const [otpDrawer, setOtpDrawer] = useState(false);
  const [newOtpSent, setNewOtpSent] = useState(false);

  const [otp, setOtp] = useState("");
  const [otpError, setotpError] = useState("");

  // Renewal pre-fill notice: shown only when we actually copied at least one field from the
  // tenant's previous journey, so the tenant NOTICES and re-checks it rather than tapping
  // through a possibly-stale employer. Never shown for a first-time tenant (no previous
  // journey → nothing prefilled) and never for the guardian/guarantor flow below, since a
  // guardian's employment is a different person's data, not the tenant's own history.
  const [showPrefillNotice, setShowPrefillNotice] = useState(false);
  // Guards the pre-fill effect to run at most once per mount, so it can never re-clobber a
  // field the tenant has since edited back to empty.
  const hasAppliedPrefill = useRef(false);

  const partner = localStorage.getItem("partnerIdentifier");
  const token = localStorage.getItem("user_identifier");

  const guarantorFlow = leadData?.isStudent;

  useEffect(() => {
    if (hasAppliedPrefill.current) return;
    if (guarantorFlow) return; // guardian's own details — not the tenant's history
    if (!leadData?.previousEmployment) return; // first-time tenant: nothing to pre-fill

    hasAppliedPrefill.current = true;
    const result = applyWorkDetailsPrefill(leadData.previousEmployment, formData);
    if (result.employmentType) {
      setEmploymentType(result.employmentType);
    }
    if (result.prefilled) {
      setFormData(result.formData);
      setShowPrefillNotice(true);
    }
  }, [leadData?.previousEmployment, guarantorFlow]);

  const handleResendOtp = async () => {
    try {
      setOtp("");
      setVerifying(true);
      setotpError("");
      const res = await updateFunction({ email: formData?.workEmail }, "user/resend-email");
      const failure = otpSendFailureMessage(res);
      // Stay in the drawer: the code sent earlier may still have arrived.
      if (failure) {
        setotpError(failure);
        return;
      }
      setNewOtpSent(true);
    } catch (error) {
      setError(error?.message || "Please try again after some time.");
      setOtpDrawer(false);
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    setError("");
  }, [formData, employmentType]);

  const sendWorkDetails = async () => {
    try {
      setLoading(true);
      const requiredFields = requiredWorkFields(employmentType, rules);

      const allFieldsPresent = requiredFields.every((field) => formData[field]);

      if (!allFieldsPresent) {
        setError("All Fields are Required");
        return;
      }

      // if (employmentType === "SELF_EMPLOYED") {
      //   // For self employed, directly navigate to onboarding
      //   const userIdentifier = localStorage.getItem("user_identifier");
      //   const nextStep =
      //     navigationRules[partner]?.fromWorkDetailsTo ||
      //     navigationRules.default.fromWorkDetailsTo;
      //   navigate(`/b/${userIdentifier}?step=${nextStep}&partner=${partner}`);
      //   return;
      // }

      // // For SALARIED, proceed with OTP verification as before
      try {
        const res = await updateFunction(
          workDetailsPayload(formData, employmentType, rules),
          "user/work-details",
        );

        if (res.status === "success") {
          const userIdentifier = localStorage.getItem("user_identifier");
          // Bureau-only credit path → congrats (the server already marked banking done); a dead
          // end (`eligible: false`: marked not eligible, banking locked) → the not-eligible screen;
          // otherwise the partner's next step, as before. See workDetailsNextStep.
          const nextStep = workDetailsNextStep(res, partner, navigationRules);
          navigate(`/b/${userIdentifier}?step=${nextStep}&partner=${partner}`);
        } else {
          setError(res.message);
        }
      } catch (e) {
        setError("Error: " + e);
      }
    } catch (error) {
      setError("ERR: " + error);
    } finally {
      setLoading(false);
    }
  };

  const handelClick = async () => {
    try {
      setLoading(true);
      setError("");

      // Validate based on employment type
      if (employmentType === "SALARIED") {
        if (!requiredWorkFields("SALARIED", rules).every((field) => formData?.[field])) {
          setError("All Fields are Required");
          return;
        }

        if (formData?.officeAddress?.length < 5) {
          setError("Please enter correct office address");
          return;
        }

        if (!normaliseSalary(formData?.income)) {
          setError("Please enter your monthly salary in numbers only, e.g. 45000");
          return;
        }

        if (needsWorkEmailOtp("SALARIED", rules)) {
          if (!isValidEmail(formData?.workEmail)) {
            setError("Not a valid work email");
            return;
          }

          const isWorkEmailValid = await isWorkEmail(formData.workEmail);
          if (!isWorkEmailValid) {
            setError("Not a valid work email");
            return;
          }
        }
      } else if (employmentType === "INTERN") {
        console.log(formData);
        if (!formData?.internshipDuration) {
          setError("All Fields are Required");
          return;
        }
      } else if (employmentType === "SELF_EMPLOYED") {
        // Self employed validation
        if (
          !formData?.businessName ||
          !formData?.turnover ||
          !formData?.registrationYear
        ) {
          setError("All Fields are Required");
          return;
        }

        if (isNaN(formData?.turnover) || formData?.turnover <= 0) {
          setError("Please enter valid turnover amount");
          return;
        }

        const currentYear = new Date().getFullYear();
        if (
          isNaN(formData?.registrationYear) ||
          formData?.registrationYear < 1900 ||
          formData?.registrationYear > currentYear
        ) {
          setError("Please enter valid registration year");
          return;
        }
      }

      if (employmentType === "SELF_EMPLOYED") {
        // For self employed, directly send work details without OTP
        await sendWorkDetails();
        return;
      }

      // A salaried tenant whose lender asks for no work email: no OTP step, send directly.
      if (employmentType === "SALARIED" && !needsWorkEmailOtp(employmentType, rules)) {
        await sendWorkDetails();
        return;
      }

      if (employmentType === "INTERN") {
        // await sendWorkDetails();
        await updateFunctionB2B(
          { isStudent: true },
          "b2b/onboard/updateanything",
          "",
          "",
          token,
        );
        navigate(`/b/${token}`);
        return;
      }

      // For SALARIED, proceed with OTP flow
      setLoading(true);
      try {
        const res = await updateFunction({ email: formData?.workEmail }, "user/send-email");
        const failure = otpSendFailureMessage(res);
        if (failure) {
          // No OTP drawer for a code that was never sent.
          setError(failure);
          return;
        }
        setOtpDrawer(true);
      } catch (error) {
        setError(error?.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      setError("ERR: " + error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      setVerifying(true);
      const response = await updateFunction(
        {
          otp: `${otp}`,
        },
        "user/verify-email",
      );

      if (response?.status === "success") {
        await sendWorkDetails();
        const platform =
          navigator?.userAgentData?.platform ||
          navigator.platform ||
          localStorage.getItem("UTM_SOURCE");
        Promise.allSettled([
          trackConsent("OTP", "Work Email", formData.workEmail, platform, ""),
        ]).then((results) => {
          console.log("Consent API calls settled:", results);
        });
      } else {
        setotpError("Incorrect OTP. Please try again.");
      }
    } catch (error) {
      setotpError("Something went wrong. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const startOnboardingJourney = async () => {
    try {
      const result = await getDataB2B("b2b/onboard/start", token);
      setJourneyToken(result?.token);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  useEffect(() => {
    if (token) {
      const timeoutId = setTimeout(() => {
        startOnboardingJourney();
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [token]);

  if (theme?.textColor) {
    return (
      <Div100vh
        className={`flex flex-col justify-between py-4 px-6 gap-8 overflow-scroll ${theme?.backgroundColor} ${theme?.text}`}
      >
        <Header
          heading={guarantorFlow ? `Guardian Work Details` : `Work Details`}
          subheading={
            guarantorFlow
              ? `We need these details to verify your guardian's employment details`
              : `We need these details to verify your employment details`
          }
        />
        <div className="flex flex-col justify-start h-full gap-4">
          {showPrefillNotice && (
            <div className="w-full p-2 px-4 bg-blue-400/10 text-blue-500 rounded-lg text-sm">
              We've filled this in from your last application — please check
              it's still correct.
            </div>
          )}
          {/* Employment Type Selector */}
          <div className="flex items-center gap-4 mb-4">
            {offeredTypes.map((type) => {
              if (type.id === "INTERN" && guarantorFlow) {
                return <></>;
              }
              return (
                <Button
                  variant="outline"
                  key={type.id}
                  className={`px-4 py-2 rounded-lg  ${
                    theme?.primaryButtonBgColor
                  } ${theme?.text} ${
                    employmentType === type.id
                      ? `${theme?.headingColor || "bg-blue-500"} text-white`
                      : "bg-gray-200 text-gray-700"
                  }`}
                  onClick={() => setEmploymentType(type.id)}
                >
                  {type.label}
                </Button>
              );
            })}
          </div>

          <div>
            <DynamicInputForm
              formConfig={
                employmentType === "SALARIED"
                  ? salariedFields(salariedConfig, rules)
                  : employmentType === "SELF_EMPLOYED"
                    ? selfEmployedConfig
                    : internConfig
              }
              formData={formData}
              setFormData={setFormData}
              setError={setError}
            />
            {error && (
              <div className="w-full p-2 px-4 bg-red-400/10 text-red-400 rounded-lg mt-4">
                {error}
              </div>
            )}
          </div>

          {/* {employmentType === "SALARIED" && (
            <div className="space-y-2">
              <div className="text-secondary">
                Have you received your salary of{" "}
                {moment().subtract(1, "month").format("MMM")} month?
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className={`px-4 py-2 rounded-lg  ${
                    theme?.primaryButtonBgColor
                  } ${theme?.text} ${
                    salaryReceivedCheck
                      ? `${theme?.headingColor || "bg-blue-500"} text-white`
                      : "bg-gray-200 text-gray-700"
                  }`}
                  onClick={() => setSalaryReceivedCheck(!salaryReceivedCheck)}
                >
                  {"YES"}
                </Button>

                <Button
                  variant="outline"
                  className={`px-4 py-2 rounded-lg  ${
                    theme?.primaryButtonBgColor
                  } ${theme?.text} ${
                    !salaryReceivedCheck
                      ? `${theme?.headingColor || "bg-blue-500"} text-white`
                      : "bg-gray-200 text-gray-700"
                  }`}
                  onClick={() => setSalaryReceivedCheck(!salaryReceivedCheck)}
                >
                  {"NO"}
                </Button>
              </div>
            </div>
          )} */}
        </div>

        <CustomDrawer isOpen={otpDrawer} theme={theme} removePadding>
          <div className="p-8">
            <GoBackButton customBackSubmit={() => setOtpDrawer(false)} />
            <div className={`w-full text-left ${theme?.textColor}`}>
              <div className={`heading mb-2 mt-12 ${theme?.textColor}`}>
                Enter OTP
              </div>
              <div
                className={`subheading font-light text-sm ${theme?.textColor}`}
              >
                Sent to{" "}
                <span className="font-light text-sm">
                  {formData?.workEmail || "your email"}
                </span>{" "}
                <span
                  onClick={() => setOtpDrawer(false)}
                  className={`underline text-sm ${
                    theme?.textColor ? theme?.textColor : "text-[#ccc]"
                  }`}
                >
                  EDIT
                </span>
              </div>
            </div>
            <div className="flex-grow overflow-auto mt-5">
              <OTPInput
                className="rounded-none"
                inputType="tel"
                value={otp}
                numInputs={config.OTP_LENGTH}
                onChange={setOtp}
                containerStyle={{
                  display: "flex",
                  justifyContent: "center",
                }}
                isInputNum={true}
                renderInput={(props) => (
                  <input
                    {...props}
                    type="tel"
                    style={{ border: "1px solid #444", margin: "4px" }}
                    className={`border-[#999] border w-10 text-center h-12 ${theme?.textColor} text-black text-lg bg-transparent justify-around`}
                  />
                )}
                pattern="[1-9]{1}[0-9]{9}"
                shouldAutoFocus
              />
              {otpError && (
                <div className="w-full p-2 px-4 bg-red-400/10 text-red-400 rounded-lg">
                  {otpError}
                </div>
              )}
              <div className="w-full text-center px-4">
                <span
                  className={`text-2xs ${
                    theme?.textColor ? theme?.textColor : "text-[#ccc]"
                  }`}
                >
                  Please check your spam folder as well
                </span>
              </div>
              {!verifying && (
                <div className="w-full px-4 pb-4">
                  <div
                    className={`text-center subheading text-sm ${theme?.textColor}`}
                  >
                    <span>{newOtpSent ? "" : "Didn't get the code?"}</span>
                    <span> </span>
                    <span
                      onClick={() => handleResendOtp()}
                      className={`font-semibold cursor-pointer text-sm ${
                        theme?.textColor ? "text-[#1987fb]" : "text-[#ccc]"
                      }`}
                    >
                      Resend it
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="pt-6">
              <PrimaryButton
                text="Verify"
                handleClick={() => handleVerifyOtp()}
                disabled={otp?.length < config.OTP_LENGTH}
                loading={verifying}
                error={error}
              />
            </div>
          </div>
        </CustomDrawer>

        <div className="space-y-2">
          <PrimaryButton
            text="Confirm"
            handleClick={handelClick}
            loading={loading}
            error={error}
          />
          {lender && (
            <div className="flex items-center justify-center">
              <span className="text-black text-2xs italic">powered by</span>
              <a href={lender.homeUrl}>
                <img src={lender.logoUrl} alt={lender.legalName} className="inline-block max-h-4 ml-1" />
              </a>
            </div>
          )}
        </div>
      </Div100vh>
    );
  }
  return null;
}
